import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { copy } from "../copy";
import type { BeatPlayer } from "../audio";
import BetSlip, { type Refusal } from "../components/BetSlip";
import Icon from "../components/Icon";
import { PickCard } from "../components/PickCards";
import { contestHref, pickKey, type LandingLiveContest, type Pick } from "../feed";
import { insufficientCredits } from "../picking";
import { postJSON, reasonOf, withPick, type ContestDetail, type Market } from "./api";
import { backingCopy } from "./backingCopy";
import Reveal from "./Reveal";

type Props = {
  contest: ContestDetail;
  market: Market;
  onMarket: (market: Market) => void;
  // ?pick= from the homepage or ?back= from a Back Me link, resolved to a
  // producer id: open this producer's slip on arrival.
  initialPick: string | null;
  player: BeatPlayer;
  reducedMotion: boolean;
};

// The slip and cards speak the landing feed's shape, so the market read is
// mapped onto it once here. Every figure is as the server sent it.
function asLine(contest: ContestDetail, market: Market): LandingLiveContest {
  return {
    slug: contest.slug,
    name: contest.title,
    genre: contest.genre,
    ends_at: contest.ends_at,
    pool_cents: market.pool_cents,
    take_bps: market.take_bps,
    rake_bps: market.rake_bps,
    producer_bps: market.producer_bps,
    is_seeded: contest.is_seeded,
    outcomes: market.outcomes.map((o) => ({ ...o })),
  };
}

function VoidNotice({ market }: { market: Market }) {
  const v = copy.contest.vote;
  const refund = market.viewer.refund;
  return (
    <div data-testid="void-notice" className="rounded-2xl border border-ground-edge bg-ground-raised p-5">
      <div className="flex items-center gap-2 text-chrome">
        <Icon name="refund" className="h-5 w-5 text-gold" />
        <h3 className="font-headline text-xl uppercase leading-none">{v.voidedHeading}</h3>
      </div>
      <p className="mt-3 text-[13px] leading-relaxed text-chrome-dim">{market.void_reason || v.voidedGeneric}</p>
      {refund && (
        <p className="mt-2 text-[13px] font-bold text-chrome">
          {refund.paid ? v.refund(refund.amount_display) : v.refundPending(refund.amount_display)}
        </p>
      )}
    </div>
  );
}

export default function VoteTab({ contest, market, onMarket, initialPick, player, reducedMotion }: Props) {
  const v = copy.contest.vote;
  const navigate = useNavigate();
  const location = useLocation();
  const [selection, setSelection] = useState<Pick | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const line = useMemo(() => asLine(contest, market), [contest, market]);
  const open = market.status === "open";
  const anonymous = market.viewer.reason === "anonymous";
  // An anonymous fan sees Back and is sent to log in; anyone else sees it
  // only when the service would take the pick.
  const showBack = open && (market.viewer.can_back || anonymous);

  // An anonymous fan arriving on a link sees the slip and its payout too;
  // Place Pick is where they are asked to log in.
  const opened = useRef(false);
  useEffect(() => {
    if (opened.current || !initialPick || !open || !(market.viewer.can_back || anonymous)) return;
    const outcome = line.outcomes.find((o) => o.producer_id === initialPick);
    if (outcome) setSelection({ contest: line, outcome });
    opened.current = true;
  }, [initialPick, open, market.viewer.can_back, anonymous, line]);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(t);
  }, [toast]);

  if (market.status === "voided") return <VoidNotice market={market} />;
  if (market.status === "settled") return <Reveal market={market} reducedMotion={reducedMotion} />;

  const onBack = (pick: Pick) => {
    if (anonymous) {
      navigate(`/login?next=${encodeURIComponent(contestHref(contest.slug, pick.outcome.producer_id))}`);
      return;
    }
    setSelection(pick);
  };

  // Not enough credits: say so, then send the fan to checkout. The producer
  // page's co-sign shares this path.
  const insufficient = (amountCents: number): Refusal =>
    insufficientCredits(amountCents, market.viewer.balance_cents ?? 0, {
      reason: v.insufficient,
      noCheckout: v.insufficientNoCheckout,
    });

  const place = async (amountCents: number, idempotencyKey: string): Promise<string | Refusal | null> => {
    if (!selection?.outcome.producer_id) return v.failed;
    if (anonymous) {
      // Back to this same link after login, so the slip opens again.
      navigate(`/login?next=${encodeURIComponent(location.pathname + location.search)}`);
      return null;
    }
    const reply = await postJSON("/v1/web/market/pick", {
      market_id: market.market_id,
      producer_id: selection.outcome.producer_id,
      amount_cents: amountCents,
      idempotency_key: idempotencyKey,
    });
    if (reply.status === 402) return insufficient(amountCents);
    if (!reply.ok) return reasonOf(reply) ?? v.failed;
    onMarket(withPick(market, reply.body));
    setToast(v.placed(selection.outcome.username));
    setSelection(null);
    return null;
  };

  const backers = market.outcomes.reduce((sum, o) => sum + o.backer_count, 0);
  const names = new Map(market.outcomes.map((o) => [o.producer_id, o.username]));
  const stats = [
    { label: v.pool, value: market.pool_display, gold: true, testId: "vote-pool" },
    { label: v.backers, value: String(backers), gold: false, testId: "vote-backers" },
    { label: v.producers, value: String(market.outcomes.length), gold: false, testId: "vote-producers" },
  ];

  return (
    <div>
      <section data-testid="pool-header" className="rounded-2xl border border-ground-edge bg-ground-raised p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-headline text-xl uppercase leading-none text-chrome">{v.heading}</h3>
          {!open && (
            <span
              data-testid="picks-closed"
              className="rounded-full border border-ground-edge bg-ground px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-chrome"
            >
              {v.picksClosed}
            </span>
          )}
        </div>
        <p className="mt-3 max-w-xl text-[12px] leading-relaxed text-chrome-dim">{open ? v.sub : v.lockedNote}</p>
        <div className="mt-5 grid grid-cols-3 gap-3">
          {stats.map((s) => (
            <div key={s.label} className="min-w-0 rounded-lg border border-ground-edge bg-ground p-3">
              <p className="text-[10px] uppercase tracking-[0.18em] text-chrome-dim">{s.label}</p>
              <p
                data-testid={s.testId}
                className={`bf-tabular mt-1.5 truncate font-headline text-xl leading-none ${s.gold ? "text-gold" : "text-chrome"}`}
              >
                {s.value}
              </p>
            </div>
          ))}
        </div>
        <p data-testid="pool-fee" className="mt-4 text-[11px] font-bold leading-relaxed text-chrome">
          {market.backing_display
            ? backingCopy.fee(market.fee_display, market.producer_cut_display, market.backing_display)
            : v.fee(market.fee_display, market.producer_cut_display)}
        </p>
        {market.bonus_display && (
          <p data-testid="pool-bonus" className="mt-1 text-[11px] font-bold leading-relaxed text-money">
            {v.bonus(market.bonus_display)}
          </p>
        )}
        {market.house_display && (
          <p data-testid="pool-house" className="mt-1 text-[11px] leading-relaxed text-chrome-dim">
            {v.house(market.house_display)}
          </p>
        )}
        {open && market.min_stake_display && market.per_user_cap_display && (
          <p className="mt-1 text-[11px] leading-relaxed text-chrome-dim">
            {v.limits(market.min_stake_display, market.per_user_cap_display)}
          </p>
        )}
        {market.viewer.balance_display && (
          <p className="mt-3 text-[11px] uppercase tracking-[0.14em] text-chrome-dim">
            {v.balance}{" "}
            <span data-testid="vote-balance" className="bf-tabular font-bold text-chrome">
              {market.viewer.balance_display}
            </span>
          </p>
        )}
        {market.viewer.backing && (
          <div data-testid="backing-earned" className="mt-3 rounded-lg border border-money/30 bg-money/5 p-3">
            <p className="bf-tabular text-[13px] font-bold leading-none text-money">
              {backingCopy.earned(market.viewer.backing.earned_display, market.viewer.backing.backers)}
            </p>
            <p className="mt-1.5 text-[11px] leading-relaxed text-chrome-dim">{backingCopy.earnedSub}</p>
          </div>
        )}
        {market.viewer.picks.length > 0 && (
          <div className="mt-3">
            <p className="text-[10px] uppercase tracking-[0.18em] text-chrome-dim">{v.yourPicks}</p>
            <ul data-testid="viewer-picks" className="mt-1.5 flex flex-wrap gap-2">
              {market.viewer.picks.map((p) => (
                <li key={p.producer_id} className="bf-tabular rounded-full border border-money/30 bg-money/5 px-2.5 py-1 text-[11px] font-bold text-money">
                  @{names.get(p.producer_id ?? "") ?? "—"} · {p.amount_display}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {open && market.viewer.reason === "entrant" && (
        <p data-testid="entrant-note" className="mt-4 rounded-lg border border-ground-edge bg-ground-raised p-3 text-[12px] text-chrome-dim">
          {v.entrant}
        </p>
      )}
      {open && market.viewer.reason === "unverified" && (
        <p className="mt-4 rounded-lg border border-ground-edge bg-ground-raised p-3 text-[12px] text-chrome-dim">
          {v.unverified}{" "}
          <a href="/send-verification" className="bf-focusable font-bold text-gold hover:text-gold-light">
            {v.verify}
          </a>
        </p>
      )}

      <div className="mt-6 space-y-3">
        {line.outcomes.map((outcome) => {
          const pick = { contest: line, outcome };
          return (
            <PickCard key={pickKey(pick)} pick={pick} player={player} onBack={onBack} variant="line" canBack={showBack} />
          );
        })}
      </div>

      <p className="mt-5 text-[11px] leading-relaxed text-chrome-dim">{v.disclaimer}</p>

      {toast && (
        <p
          role="status"
          data-testid="pick-toast"
          className="fixed inset-x-4 bottom-4 z-[70] mx-auto max-w-sm rounded-lg border border-money/40 bg-ground-raised p-3 text-center text-[12px] font-bold text-money shadow-money"
        >
          {toast}
        </p>
      )}

      {selection && (
        <BetSlip key={pickKey(selection)} pick={selection} onClose={() => setSelection(null)} onPlace={place} />
      )}
    </div>
  );
}
